import { useForm, useStore } from '@tanstack/react-form'
import type { BookingNote } from '@transreport/pauk-rrms-api'
import type React from 'react'
import { useTranslation } from 'react-i18next'

import { AppButton, Modal, UnsavedChangesBlocker } from '@/components/framework'
import { selectStaffName, useAuthStore } from '@/lib/stores/auth'

import { BookingLogTableSkeleton } from './BookingLogTableSkeleton'

const formatNoteDate = (createdAt: string) =>
  new Date(createdAt).toLocaleString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })

interface AddAssistanceNoteModalProps {
  isOpen: boolean
  onClose: () => void
  bookingLog: BookingNote[]
  isBookingLogLoading: boolean
  bookingLogError: unknown
  onAddNote: (note: string) => void
}

export const AddAssistanceNoteModal: React.FC<AddAssistanceNoteModalProps> = ({
  isOpen,
  onClose,
  bookingLog,
  isBookingLogLoading,
  bookingLogError,
  onAddNote,
}) => {
  const { t } = useTranslation()
  const staffName = useAuthStore(selectStaffName)
  const form = useForm({
    defaultValues: {
      noteText: '',
    },
  })
  const noteText = useStore(form.store, (state) => state.values.noteText)
  const isDirty = useStore(form.store, (state) => state.isDirty)

  const handleClose = () => {
    form.reset({ noteText: '' })
    onClose()
  }

  const handleAdd = () => {
    onAddNote(noteText.trim())
    form.reset({ noteText: '' })
    onClose()
  }

  const isAddDisabled = noteText.trim().length === 0

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('assistanceNotes.title')} size="xl">
      <UnsavedChangesBlocker isDirty={isDirty} />
      <div className="space-y-4">
        <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
          <section className="rounded bg-white p-4">
            <div className="space-y-4 text-base text-gray-900">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,12rem)_minmax(0,1fr)] md:items-center">
                <div className="text-sm font-semibold text-gray-900">
                  {t('assistanceNotes.staff')}
                </div>
                <div>{staffName ?? t('assistanceNotes.username')}</div>
              </div>

              <div>
                <textarea
                  id="add-note-textarea"
                  aria-label={t('assistanceNotes.addNoteLabel')}
                  value={noteText}
                  onChange={(event) => form.setFieldValue('noteText', event.target.value)}
                  rows={4}
                  className="w-full rounded-input border border-border-light px-4 py-3 text-base focus:ring-2 focus:ring-primary focus:outline-none"
                  placeholder={t('assistanceNotes.notePlaceholder')}
                />
              </div>
            </div>
          </section>

          <section className="rounded bg-white p-4">
            <h3 className="mb-3 text-sm font-semibold tracking-wide text-gray-500 uppercase">
              {t('assistanceNotes.bookingLog')}
            </h3>

            {isBookingLogLoading ? (
              <BookingLogTableSkeleton loadingLabel={t('assistanceNotes.loadingBookingLog')} />
            ) : bookingLogError ? (
              <p className="text-sm text-red-700">{t('assistanceNotes.bookingLogError')}</p>
            ) : bookingLog.length === 0 ? (
              <p className="text-sm text-gray-500">{t('assistanceNotes.noBookingLogNotes')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full border-separate border-spacing-0 text-sm text-gray-800">
                  <caption className="sr-only">
                    {t('assistanceNotes.bookingLogTableCaption')}
                  </caption>
                  <colgroup>
                    <col className="w-40" />
                    <col className="w-36" />
                    <col />
                  </colgroup>
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="border-b border-gray-200 p-2 text-left font-semibold text-gray-700"
                      >
                        {t('assistanceNotes.createdBy')}
                      </th>
                      <th
                        scope="col"
                        className="border-b border-gray-200 p-2 text-left font-semibold text-gray-700"
                      >
                        {t('assistanceNotes.createdAt')}
                      </th>
                      <th
                        scope="col"
                        className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700"
                      >
                        {t('assistanceNotes.noteText')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookingLog.map((note) => (
                      <tr key={note.id}>
                        <td className="border-b border-gray-100 p-2 align-top">
                          {note.created_by?.trim() || t('assistanceNotes.unknownUser')}
                        </td>
                        <td className="border-b border-gray-100 p-2 align-top whitespace-nowrap text-gray-700">
                          {formatNoteDate(note.created_at)}
                        </td>
                        <td className="border-b border-gray-100 px-3 py-2 align-top break-words whitespace-pre-wrap">
                          {note.text}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        <div className="sticky bottom-0 z-10 -mx-modal-padding mt-1 flex justify-end gap-gap-sm border-t border-border-lighter bg-white px-modal-padding pt-3 pb-0.5">
          <AppButton variant="outline" size="md" onClick={handleClose}>
            {t('assistanceNotes.cancel')}
          </AppButton>
          <AppButton variant="primary" size="md" onClick={handleAdd} disabled={isAddDisabled}>
            {t('assistanceNotes.add')}
          </AppButton>
        </div>
      </div>
    </Modal>
  )
}
